import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import noEngTeamImg from '@/assets/images/noengteam.webp'
import scalableImg  from '@/assets/images/scalablesystems.webp'
import executionImg from '@/assets/images/execution.webp'
import clearImg     from '@/assets/images/clear.webp'
import vector1      from '@/assets/images/Vector1.webp'
import vector2      from '@/assets/images/Vector2.webp'
import vector3      from '@/assets/images/Vector3.webp'
import vector4      from '@/assets/images/Vector4.webp'

const ITEMS = [
  {
    id: 1,
    title: 'No Engineering Team?',
    description: 'No reliable team to build, manage or scale your product.',
    image: noEngTeamImg,
    shade: vector1,
    imgW: 658, imgH: 606,
  },
  {
    id: 2,
    title: 'No Scalable Systems?',
    description: "Your production isn't built to handle growth, performance and long-term scale.",
    image: scalableImg,
    shade: vector2,
    imgW: 799, imgH: 532,
  },
  {
    id: 3,
    title: 'No Control Over Execution?',
    description: 'You lack visibility, coordination, and process ownership across development and delivery.',
    image: executionImg,
    shade: vector3,
    imgW: 808, imgH: 564,
  },
  {
    id: 4,
    title: 'No Clear Path to MVP?',
    description: 'Struggling to turn your ideas into a working product that can be launched and validated.',
    image: clearImg,
    shade: vector4,
    imgW: 822, imgH: 548,
  },
]

const HOLD_MS = 3000

export function ProblemsSection() {
  const [active, setActive]   = useState(0)
  const [barTop, setBarTop]   = useState(0)
  const [barH, setBarH]       = useState(60)
  const itemRefs              = useRef<(HTMLButtonElement | null)[]>([])
  const intervalRef           = useRef<ReturnType<typeof setInterval> | null>(null)
  const sectionRef            = useRef<HTMLElement | null>(null)
  const [inView, setInView]   = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting)
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const el = itemRefs.current[active]
    if (el) {
      setBarTop(el.offsetTop)
      setBarH(el.offsetHeight)
    }
  }, [active])

  useEffect(() => {
    if (inView) {
      intervalRef.current = setInterval(() => {
        setActive((a) => (a + 1) % ITEMS.length)
      }, HOLD_MS)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [inView])

  const handleClick = (i: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setActive(i)
    if (inView) {
      intervalRef.current = setInterval(() => {
        setActive((a) => (a + 1) % ITEMS.length)
      }, HOLD_MS)
    }
  }

  const cur = ITEMS[active]

  return (
    <section
      ref={sectionRef}
      id="problems"
      className="bg-white pt-[40px] sm:pt-[60px] lg:pt-[180px]"
      aria-label="The Problem"
    >
      <div className="container-site">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-col gap-3 sm:gap-4 lg:mb-14 lg:gap-6"
        >
          <span
            className="font-sans text-[13px] font-bold not-italic uppercase leading-[17px] tracking-[0.06px] text-[#D4575B] sm:text-[15px] lg:text-[18px]"
            style={{ leadingTrim: 'both', textEdge: 'cap' } as React.CSSProperties}
          >
            The Problem
          </span>
          <div className="w-full max-w-[1240px]">
            <h2 className="font-sans text-[26px] font-medium not-italic leading-[30px] tracking-[-0.5px] text-[#2D2D2D] sm:text-[34px] sm:leading-[38px] md:text-[40px] md:leading-[44px] lg:text-[48px] lg:leading-[50px] lg:tracking-[-1px]">
              Building Software Is Hard. Scaling It Is Even Harder.
            </h2>
            <p className="font-sans text-[26px] font-medium not-italic leading-[30px] tracking-[-0.5px] text-[#6E739D] sm:text-[34px] sm:leading-[38px] md:text-[40px] md:leading-[44px] lg:text-[48px] lg:leading-[50px] lg:tracking-[-1px]">
              Founder and businesses struggle to build and scale reliable software systems.
            </p>
          </div>
        </motion.div>

        {/* Card */}
        <div className="flex flex-col lg:flex-row w-full max-w-[1240px] xl:h-[606px] mx-auto rounded-[16px] sm:rounded-[20px] md:rounded-[24px] lg:rounded-[27px] bg-[#FAFAFA] overflow-hidden">

          {/* Left — item list */}
          <div className="relative shrink-0 w-full lg:w-[380px] xl:w-[416px] p-5 sm:p-7 lg:p-10 flex flex-col justify-center">

            {/* Sliding bar */}
            <motion.div
              className="absolute left-0 w-[5px] bg-[#5AB1E8]"
              animate={{ top: barTop, height: barH }}
              transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
            />

            <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
              {ITEMS.map((item, i) => (
                <button
                  key={item.id}
                  ref={el => { itemRefs.current[i] = el }}
                  onClick={() => handleClick(i)}
                  className="text-left focus:outline-none group"
                >
                  <h3 className={`font-sans font-medium text-[16px] sm:text-[18px] xl:text-[24px] leading-normal transition-colors duration-300 ${
                    i === active ? 'text-[#292929]' : 'text-[#737373] group-hover:text-[#525252]'
                  }`}>
                    {item.title}
                  </h3>
                  <p className={`font-sans font-normal text-[13px] sm:text-[14px] xl:text-[16px] leading-normal mt-1 transition-colors duration-300 ${
                    i === active ? 'text-[#5E638D]' : 'text-[#737373] group-hover:text-[#525252]'
                  }`}>
                    {item.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Right — shade + illustration */}
          <div className="relative flex-1 overflow-hidden min-h-[220px] sm:min-h-[300px] md:min-h-[380px] lg:min-h-0">

            {/* Shade background — crossfades per item */}
            <AnimatePresence>
              <motion.img
                key={`shade-${cur.id}`}
                src={cur.shade}
                alt=""
                aria-hidden
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />
            </AnimatePresence>

            {/* SVG illustration — crossfades per item */}
            <div className="relative z-10 flex items-center justify-center w-full h-full p-4 sm:p-6 lg:p-6 xl:p-0">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`img-${cur.id}`}
                  src={cur.image}
                  alt={cur.title}
                  draggable={false}
                  width={cur.imgW}
                  height={cur.imgH}
                  className="object-contain w-[220px] h-auto sm:w-[320px] md:w-[440px] lg:w-[560px] xl:w-full xl:h-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                />
              </AnimatePresence>
            </div>

          </div>
        </div>

      </div>
    </section>
  )
}
